import React, { useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Drawer
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SensorsIcon from '@mui/icons-material/Sensors';
import ParkingEntityDrawer from './ParkingEntityDrawer';
import CrowdCameraEntityDrawer from './CrowdCameraEntityDrawer';
import WirelessAccessPointEntityDrawer from './WirelessAccessPointEntityDrawer';
import ElectricityEntityDrawer from './ElectricityEntityDrawer';
import TrashRecycleEntityDrawer from './TrashRecycleEntityDrawer';
import PaperTowelEntityDrawer from './PaperTowelEntityDrawer';
import FreezerEntityDrawer from './FreezerEntityDrawer';
import DoorEntityDrawer from './DoorEntityDrawer';
import StormDrainEntityDrawer from './StormDrainEntityDrawer';
import CameraEntityDrawer from './CameraEntityDrawer';

const EntityDrawer = ({ 
  open, 
  onClose, 
  selectedEntity, 
  entityDetails, 
  loadingEntityDetails 
}) => {
  const chartRef = useRef(null);

  // Get entity type from entityDetails
  const getEntityType = () => {
    if (!entityDetails?.entityTypeId?.name) return 'parking';
    
    const entityTypeName = entityDetails.entityTypeId.name.toLowerCase();
    if (entityTypeName.includes('parking')) return 'parking';
    if (entityTypeName.includes('crowd') || entityTypeName.includes('people')) return 'crowdCamera';
    if (entityTypeName.includes('camera') || entityTypeName.includes('cctv') || entityTypeName.includes('video')) return 'camera';
    if (entityTypeName.includes('wifi') || entityTypeName.includes('wireless') || entityTypeName.includes('access')) return 'wirelessAccessPoint';
    if (entityTypeName.includes('electric') || entityTypeName.includes('power') || entityTypeName.includes('Light')) return 'electricity';
    if (
      entityTypeName.includes('trash') ||
      entityTypeName.includes('recycl') ||
      entityTypeName.includes('waste') ||
      entityTypeName.includes('bin')
    ) return 'trashRecycle';
    if (
      entityTypeName.includes('paper') ||
      entityTypeName.includes('towel') ||
      entityTypeName.includes('dispenser')
    ) return 'paperTowel';
    if (
      entityTypeName.includes('freezer') ||
      entityTypeName.includes('fridge') ||
      entityTypeName.includes('refrigeration')
    ) return 'freezer';
    if (
      entityTypeName.includes('door') ||
      entityTypeName.includes('gate')
    ) return 'door';
    if (
      entityTypeName.includes('storm') ||
      entityTypeName.includes('drain') ||
      entityTypeName.includes('sewer')
    ) return 'stormDrain';
    
    return 'parking'; // default
  };

  const currentEntityType = getEntityType();

  const renderContentByEntityType = () => {
    switch (currentEntityType) {
      case 'parking':
        return <ParkingEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'crowdCamera':
        return <CrowdCameraEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'camera':
        return <CameraEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'wirelessAccessPoint':
        return <WirelessAccessPointEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'electricity':
        return <ElectricityEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'trashRecycle':
        return <TrashRecycleEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'paperTowel':
        return <PaperTowelEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'freezer':
        return <FreezerEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'door':
        return <DoorEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      case 'stormDrain':
        return <StormDrainEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
      default:
        return <ParkingEntityDrawer entityDetails={entityDetails} chartRef={chartRef} />;
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 450,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2, height: '100%', overflow: 'auto' }}>
        {selectedEntity && (
          <Box>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                {selectedEntity.name}
              </Typography>
              <IconButton onClick={onClose}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Content */}
            {loadingEntityDetails ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : entityDetails ? (
              <Box>
                {/* Entity Overview */}
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Entity Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          Type: {entityDetails.entityTypeId?.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="textSecondary">
                          ID: {entityDetails._id}
                        </Typography>
                      </Grid>
                      {entityDetails.isGeoLocated && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="textSecondary">
                            Location: {entityDetails.keys?.find(k => k.keyName === 'latitude')?.value}, {entityDetails.keys?.find(k => k.keyName === 'longitude')?.value}
                          </Typography>
                        </Grid>
                      )}
                      {Array.isArray(entityDetails.keys) && entityDetails.keys.length > 0 && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" gutterBottom>
                              Attributes
                            </Typography>
                          </Grid>
                          {entityDetails.keys.map((k, idx) => (
                            <Grid item xs={6} key={k._id || k.keyName || idx}>
                              <Typography variant="body2" color="textSecondary">
                                {k.keyName}: {String(k.value)}
                              </Typography>
                            </Grid>
                          ))}
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>

                {/* Entity Image */}
                {entityDetails.image?.url && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Entity Image
                      </Typography>
                      <Box
                        component="img"
                        src={entityDetails.image.url}
                        alt={entityDetails.image.fileName || 'Entity Image'}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: 300,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid #e0e0e0'
                        }}
                      />
                      {entityDetails.image.fileName && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          {entityDetails.image.fileName}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Dynamic Content Based on Entity Type */}
                {renderContentByEntityType()}

                {/* Sensors Section */}
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Sensors ({entityDetails.sensors?.length || 0})
                    </Typography>
                    {entityDetails.sensors && entityDetails.sensors.length > 0 ? (
                      <List>
                        {entityDetails.sensors.map((sensor, index) => (
                          <ListItem key={sensor._id || index} divider>
                            <ListItemIcon>
                              <SensorsIcon color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Typography variant="subtitle1">
                                    {sensor.externalId || `Sensor ${index + 1}`}
                                  </Typography>
                                  {sensor.externalId && (
                                    <Chip 
                                      label="External ID" 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined" 
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography variant="body2" color="textSecondary">
                                    Sensor ID: {sensor.sensorId}
                                  </Typography>
                                  {sensor.sensorId && entityDetails.entityTypeId?.sensors && (
                                    <Typography variant="body2" color="textSecondary">
                                      Type: {entityDetails.entityTypeId.sensors.find(s => s._id === sensor.sensorId)?.sensor_name || 'Unknown'}
                                    </Typography>
                                  )}
                                  {entityDetails.entityTypeId?.sensors && (
                                    <Typography variant="body2" color="textSecondary">
                                      Description: {entityDetails.entityTypeId.sensors.find(s => s._id === sensor.sensorId)?.description || 'No description available'}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No sensors configured for this entity.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Typography color="error">
                Failed to load entity details.
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default EntityDrawer; 